/**
 * CurrencySwitcher — sélecteur de devise d'affichage.
 * La devise du marchand reste la référence ; le visiteur peut convertir
 * l'affichage dans SA monnaie (taux de change en direct).
 * La devise locale du visiteur est détectée automatiquement et proposée
 * en tête de liste pour une conversion en un clic.
 */
import { useEffect, useRef, useState } from 'react';
import {
  CURRENCIES,
  currencyInfo,
  getDisplayCurrency,
  normalizeCurrency,
  setDisplayCurrency,
  loadExchangeRates,
  type Currency,
} from '@/lib/currency';
import { detectVisitorCurrency } from '@/lib/geoCurrency';

interface Props {
  /** Devise de référence du marchand (affichée comme option « natif »). */
  nativeCurrency?: string;
  compact?: boolean;
  dark?: boolean;
  className?: string;
}

export default function CurrencySwitcher({
  nativeCurrency = 'MAD',
  compact = false,
  dark = false,
  className = '',
}: Props) {
  const native = normalizeCurrency(nativeCurrency);
  const [current, setCurrent] = useState(getDisplayCurrency() || native);
  const [open, setOpen] = useState(false);
  const [detected, setDetected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Taux de change en direct + devise locale du visiteur
  useEffect(() => {
    loadExchangeRates();
    let active = true;
    detectVisitorCurrency().then((code) => {
      if (active && code) setDetected(code);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const code = (e as CustomEvent<{ currency?: string }>).detail?.currency;
      if (code) setCurrent(normalizeCurrency(code));
    };
    window.addEventListener('zifek-currency-changed', handler);
    return () => window.removeEventListener('zifek-currency-changed', handler);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Liste : devise du marchand en tête, puis devise détectée, puis les autres.
  const nativeInfo = currencyInfo(native);
  const detectedInfo = detected && detected !== native ? currencyInfo(detected) : null;
  const others = CURRENCIES.filter((c) => c.code !== native && c.code !== detected);
  const list: Currency[] = [nativeInfo, ...(detectedInfo ? [detectedInfo] : []), ...others].slice(0, 22);

  const select = (code: string) => {
    setCurrent(normalizeCurrency(code));
    setDisplayCurrency(normalizeCurrency(code));
    setOpen(false);
  };

  const shown = currencyInfo(current);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
          dark
            ? 'text-white/70 hover:text-white hover:bg-white/10'
            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100 border border-background-200/70'
        }`}
        title="Changer de devise"
      >
        <i className="ri-money-dollar-circle-line text-base leading-none"></i>
        {compact ? <span className="text-xs">{shown.code}</span> : <span>{shown.code}</span>}
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 min-w-[240px] max-h-[340px] overflow-y-auto bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-1 z-50">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
            Devise du marchand
          </div>
          <button
            onClick={() => select(native)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
              native === current ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'
            }`}
          >
            <span className="text-base">{nativeInfo.flag}</span>
            <span className="truncate">{nativeInfo.code}</span>
            <span className="text-xs text-foreground-400 truncate">{nativeInfo.label}</span>
            {native === current && <i className="ri-check-line ml-auto text-primary-500 text-sm"></i>}
          </button>

          {detectedInfo && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-400 border-t border-background-200/70 mt-1">
                Détectée pour vous
              </div>
              <button
                onClick={() => select(detectedInfo.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                  detectedInfo.code === current ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span className="text-base">{detectedInfo.flag}</span>
                <span className="truncate">{detectedInfo.code}</span>
                <span className="text-xs text-foreground-400 truncate">{detectedInfo.label}</span>
                {detectedInfo.code === current && <i className="ri-check-line ml-auto text-primary-500 text-sm"></i>}
              </button>
            </>
          )}

          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-400 border-t border-background-200/70 mt-1">
            Convertir l'affichage
          </div>
          {others.map((c) => (
            <button
              key={c.code}
              onClick={() => select(c.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                c.code === current
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span className="text-base">{c.flag}</span>
              <span className="truncate">{c.code}</span>
              <span className="text-xs text-foreground-400 truncate">{c.label}</span>
              {c.code === current && <i className="ri-check-line ml-auto text-primary-500 text-sm"></i>}
            </button>
          ))}

          <div className="px-3 py-2 mt-1 border-t border-background-200/70 flex items-center gap-1.5 text-[10px] text-foreground-400">
            <i className="ri-refresh-line"></i>
            Conversion basée sur les taux de change en direct
          </div>
        </div>
      )}
    </div>
  );
}